import React, { useState, useEffect, useCallback, useRef } from 'react';
import { StripeCardForm, type StripeCardFormHandle } from '@/components/billing/StripeCardForm';
import { StripeWalletButton } from '@/components/billing/StripeWalletButton';
import { isStripeConfigured } from '@/components/billing/stripeConfig';
import { motion, AnimatePresence, type Variants } from 'framer-motion';
import {
  ArrowLeft,
  CreditCard,
  Wallet,
  Building2,
  Shield,
  Lock,
  RefreshCw,
  Check,
  ChevronDown,
  ChevronUp,
  Info,
  Tag,
  AlertCircle,
  Sparkles,
  X,
} from 'lucide-react';
import { BrandedLoading, BrandedSpinner } from '@/components/common/BrandedLoading';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { billingApi } from '@/services/billingApi';
import type {
  BillingCycle,
  CouponValidationResult,
  ProcessPaymentRequest,
} from '@/types/billing';
import { useAuth } from '@/contexts/AuthContext';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const LOCAL_USER_INFO_KEY = 'five_ai_local_user_info';
const PLAN_CODE_STORAGE_KEY = 'five_ai_onboarding_plan_code';
const SELECTED_PLAN_DATA_KEY = 'five_ai_onboarding_selected_plan_data';

// ---------------------------------------------------------------------------
// Local Types
// ---------------------------------------------------------------------------

interface SelectedPlanData {
  planCode: string;
  displayName?: string;
  monthlyPrice?: number;
  yearlyPrice?: number;
  billingCycle: string;
  currency: string;
  trialEnabled?: boolean;
  trialDurationDays?: number;
  features?: string[];
  limits?: Record<string, unknown>;
  countryCode?: string;
  paymentGateway?: string;
}

interface LocalUserInfo {
  email?: string;
  signUpEmail?: string;
  firstName?: string;
  lastName?: string;
}

interface BillingFormData {
  email: string;
  firstName: string;
  lastName: string;
  cardNumber: string;
  expiryDate: string;
  cvv: string;
  cardholderName: string;
}

interface FormErrors {
  email?: string;
  firstName?: string;
  lastName?: string;
  cardNumber?: string;
  expiryDate?: string;
  cvv?: string;
  cardholderName?: string;
}

type PageState = 'loading' | 'form' | 'processing' | 'success' | 'error';
type PaymentMethodTab = 'card' | 'digital-wallet' | 'bank-transfer';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface PaymentCheckoutPageProps {
  sessionId?: string;
  onComplete?: () => void;
  onBack?: () => void;
}

// ---------------------------------------------------------------------------
// Animation Variants
// ---------------------------------------------------------------------------

/* eslint-disable @typescript-eslint/no-explicit-any */
const pageVariants: Variants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: "easeOut" },
  },
  exit: { opacity: 0, y: -12, transition: { duration: 0.3 } },
};

const staggerContainer: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.1 },
  },
};

const staggerItem: Variants = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: "easeOut" },
  },
};

const shakeVariants: Variants = {
  shake: {
    x: [0, -8, 8, -6, 6, -3, 3, 0],
    transition: { duration: 0.5 },
  },
};

const successCheckVariants: Variants = {
  hidden: { scale: 0, opacity: 0 },
  visible: {
    scale: 1,
    opacity: 1,
    transition: {
      type: 'spring',
      stiffness: 260,
      damping: 20,
      delay: 0.1,
    },
  },
};

const successRingVariants: Variants = {
  hidden: { scale: 0.8, opacity: 0 },
  visible: {
    scale: 1,
    opacity: 1,
    transition: {
      type: 'spring',
      stiffness: 200,
      damping: 15,
    },
  },
};

const sparkleVariants: any = {
  hidden: { scale: 0, opacity: 0 },
  visible: (i: number) => ({
    scale: [0, 1.2, 0],
    opacity: [0, 1, 0],
    transition: {
      duration: 0.8,
      delay: 0.3 + i * 0.1,
      ease: 'easeOut',
    },
  }),
};

// ---------------------------------------------------------------------------
// Utility Helpers
// ---------------------------------------------------------------------------

function formatCardNumber(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 16);
  return digits.replace(/(\d{4})(?=\d)/g, '$1 ');
}

function formatExpiry(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 4);
  if (digits.length >= 3) {
    return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  }
  return digits;
}

function formatCurrency(amount: number, currency: string = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
  }).format(amount);
}

function validateEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function validateCardNumber(card: string): boolean {
  const digits = card.replace(/\s/g, '');
  return /^\d{13,19}$/.test(digits);
}

function validateExpiry(expiry: string): boolean {
  if (!/^\d{2}\/\d{2}$/.test(expiry)) return false;
  const [monthStr, yearStr] = expiry.split('/');
  const month = parseInt(monthStr, 10);
  const year = parseInt(yearStr, 10) + 2000;
  if (month < 1 || month > 12) return false;
  const now = new Date();
  const expiryDate = new Date(year, month);
  return expiryDate > now;
}

function validateCvv(cvv: string): boolean {
  return /^\d{3,4}$/.test(cvv);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const PaymentCheckoutPage: React.FC<PaymentCheckoutPageProps> = ({
  onComplete,
  onBack,
}) => {
  const { user } = useAuth();

  // ---- State ----
  const [pageState, setPageState] = useState<PageState>('loading');
  const [planData, setPlanData] = useState<SelectedPlanData | null>(null);
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('MONTHLY');
  const [paymentTab, setPaymentTab] = useState<PaymentMethodTab>('card');
  const [formShake, setFormShake] = useState(false);

  const [form, setForm] = useState<BillingFormData>({
    email: '',
    firstName: '',
    lastName: '',
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    cardholderName: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Coupon
  const [couponExpanded, setCouponExpanded] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponResult, setCouponResult] = useState<CouponValidationResult | null>(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState<string | null>(null);

  // Processing / Error
  const [processingProgress, setProcessingProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ---- Stripe state ----
  // The Stripe CardElement validates the card itself; we only mirror its complete/error state
  // so the "Pay" button can be enabled/disabled and inline error UI can render.
  const stripeCardRef = useRef<StripeCardFormHandle>(null);
  const [stripeCardComplete, setStripeCardComplete] = useState(false);
  const [stripeCardError, setStripeCardError] = useState<string | undefined>(undefined);
  const stripeReady = isStripeConfigured();

  // ---- Load plan data from localStorage ----
  useEffect(() => {
    try {
      const rawPlan = localStorage.getItem(SELECTED_PLAN_DATA_KEY);
      if (!rawPlan) {
        setErrorMessage('No plan selected. Please go back and select a plan.');
        setPageState('error');
        return;
      }
      const parsed: SelectedPlanData = JSON.parse(rawPlan);
      setPlanData(parsed);
      setBillingCycle((parsed.billingCycle as BillingCycle) || 'MONTHLY');

      // Pre-fill billing info
      const rawUser = localStorage.getItem(LOCAL_USER_INFO_KEY);
      const localUser: LocalUserInfo = rawUser ? JSON.parse(rawUser) : {};

      setForm((prev) => ({
        ...prev,
        email: localUser.signUpEmail || localUser.email || user?.email || '',
        firstName: localUser.firstName || user?.firstName || '',
        lastName: localUser.lastName || user?.lastName || '',
        cardholderName: [localUser.firstName, localUser.lastName]
          .filter(Boolean)
          .join(' ') || '',
      }));

      setPageState('form');
    } catch (err) {
      console.error('[PaymentCheckout] Failed to load plan data:', err);
      setErrorMessage('Failed to load plan details. Please go back and try again.');
      setPageState('error');
    }
  }, [user]);

  // ---- Price calculation ----
  const getBasePrice = useCallback((): number => {
    if (!planData) return 0;
    if (billingCycle === 'YEARLY') {
      return planData.yearlyPrice ?? (planData.monthlyPrice ?? 0) * 12;
    }
    return planData.monthlyPrice ?? 0;
  }, [planData, billingCycle]);

  const getDiscountAmount = useCallback((): number => {
    if (!couponResult?.valid || !couponResult.discountAmount) return 0;
    return couponResult.discountAmount;
  }, [couponResult]);

  const getTotalPrice = useCallback((): number => {
    const base = getBasePrice();
    const discount = getDiscountAmount();
    return Math.max(0, base - discount);
  }, [getBasePrice, getDiscountAmount]);

  const isTrialPlan = planData?.trialEnabled === true;
  const currency = planData?.currency || 'USD';

  // ---- Yearly savings ----
  const yearlySavings = useCallback((): number | null => {
    if (!planData?.monthlyPrice || !planData?.yearlyPrice) return null;
    const monthlyAnnualized = planData.monthlyPrice * 12;
    const diff = monthlyAnnualized - planData.yearlyPrice;
    return diff > 0 ? diff : null;
  }, [planData]);

  // ---- Form validation ----
  const validateForm = useCallback((): FormErrors => {
    const errs: FormErrors = {};
    if (!form.email || !validateEmail(form.email)) {
      errs.email = 'Please enter a valid email address';
    }
    if (!form.firstName.trim()) {
      errs.firstName = 'First name is required';
    }
    if (!form.lastName.trim()) {
      errs.lastName = 'Last name is required';
    }
    if (paymentTab === 'card') {
      if (!validateCardNumber(form.cardNumber)) {
        errs.cardNumber = 'Please enter a valid card number';
      }
      if (!validateExpiry(form.expiryDate)) {
        errs.expiryDate = 'Please enter a valid expiry date (MM/YY)';
      }
      if (!validateCvv(form.cvv)) {
        errs.cvv = 'Please enter a valid CVV';
      }
      if (!form.cardholderName.trim()) {
        errs.cardholderName = 'Cardholder name is required';
      }
    }
    return errs;
  }, [form, paymentTab]);

  // Form-validity gate. On the Card tab with Stripe enabled, the card-number/expiry/CVV are
  // validated by Stripe Elements (mirrored into stripeCardComplete) — skip the legacy
  // string-based card validators so the gate doesn't deadlock.
  const isFormValid = (() => {
    const errs = validateForm();
    if (paymentTab === 'card' && stripeReady) {
      delete errs.cardNumber;
      delete errs.expiryDate;
      delete errs.cvv;
      delete errs.cardholderName;
      if (!stripeCardComplete) return false;
    }
    return Object.keys(errs).length === 0;
  })();

  // ---- Field handlers ----
  const handleFieldChange = (field: keyof BillingFormData, value: string) => {
    let formatted = value;
    if (field === 'cardNumber') formatted = formatCardNumber(value);
    if (field === 'expiryDate') formatted = formatExpiry(value);
    if (field === 'cvv') formatted = value.replace(/\D/g, '').slice(0, 4);

    setForm((prev) => ({ ...prev, [field]: formatted }));

    // Clear error for this field on change
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleFieldBlur = (field: keyof BillingFormData) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const errs = validateForm();
    if (errs[field]) {
      setErrors((prev) => ({ ...prev, [field]: errs[field] }));
    }
  };

  // ---- Coupon ----
  const handleApplyCoupon = async () => {
    if (!couponCode.trim() || !planData) return;
    setCouponLoading(true);
    setCouponError(null);
    setCouponResult(null);
    try {
      const result = await billingApi.validateCoupon(couponCode.trim(), planData.planCode);
      if (result.valid) {
        setCouponResult(result);
      } else {
        setCouponError(result.message || 'Invalid coupon code');
      }
    } catch (err) {
      setCouponError((err as Error)?.message || 'Failed to validate coupon. Please try again.');
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setCouponResult(null);
    setCouponCode('');
    setCouponError(null);
  };

  // ---- Billing cycle toggle ----
  const handleBillingCycleToggle = (cycle: BillingCycle) => {
    setBillingCycle(cycle);
    // Update localStorage
    if (planData) {
      const updated = { ...planData, billingCycle: cycle };
      localStorage.setItem(SELECTED_PLAN_DATA_KEY, JSON.stringify(updated));
    }
    // Clear coupon when switching cycle (discount may differ)
    if (couponResult) {
      handleRemoveCoupon();
    }
  };

  // ---- Checkout completion (shared between success and non-prod bypass) ----
  const completeCheckout = () => {
    if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    setProcessingProgress(100);

    // Persist selected plan in localStorage
    if (planData) {
      localStorage.setItem(PLAN_CODE_STORAGE_KEY, planData.planCode);
      localStorage.setItem(
        SELECTED_PLAN_DATA_KEY,
        JSON.stringify({ ...planData, billingCycle })
      );
    }

    // Show success then auto-advance
    setTimeout(() => setPageState('success'), 400);
    setTimeout(() => {
      onComplete?.();
    }, 2400);
  };

  // ---- Submit ----
  const handleSubmit = async () => {
    const errs = validateForm();

    // Cards now use Stripe Elements — skip the legacy card-field validations on the Card tab.
    // We only need email/name/postal-ish billing details from the rest of the form.
    if (paymentTab === 'card') {
      delete errs.cardNumber;
      delete errs.expiryDate;
      delete errs.cvv;
      delete errs.cardholderName;
    }

    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      setTouched(
        Object.keys(errs).reduce((acc, key) => ({ ...acc, [key]: true }), {})
      );
      setFormShake(true);
      setTimeout(() => setFormShake(false), 600);
      return;
    }

    // Stripe card-element validation gate.
    if (paymentTab === 'card' && stripeReady && !stripeCardComplete) {
      setStripeCardError(stripeCardError ?? 'Please complete your card details.');
      setFormShake(true);
      setTimeout(() => setFormShake(false), 600);
      return;
    }

    if (!planData) return;

    setPageState('processing');
    setProcessingProgress(0);
    setErrorMessage(null);

    // Animate progress bar
    progressIntervalRef.current = setInterval(() => {
      setProcessingProgress((prev) => {
        if (prev >= 90) {
          if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
          return 90;
        }
        return prev + Math.random() * 15;
      });
    }, 300);

    try {
      // Step 1: Create subscription
      const subscription = await billingApi.createSubscription({
        userId: user?.id || 'onboarding-user',
        subscriptionPlanId: planData.planCode,
        billingCycle,
        currency,
        countryCode: planData.countryCode,
        paymentGateway: paymentTab === 'card' ? 'STRIPE' : (planData.paymentGateway || 'MOCK'),
        autoRenewal: true,
      });

      // Step 2: Process payment.
      // Card tab → Stripe Elements (real card, 3DS-aware). Other tabs → legacy backend path.
      if (paymentTab === 'card' && stripeReady) {
        const result = await stripeCardRef.current?.submit();
        if (!result) {
          throw new Error('Card form is not ready');
        }
        if (!result.ok) {
          throw new Error(result.message);
        }
        // Stripe webhook flips the persisted subscription/PaymentTransaction to ACTIVE/SUCCESS.
        // We still call processPayment so the local PaymentTransaction record gets created and
        // tied to the subscription — the gateway's create-intent already exists on Stripe.
        // (Future: skip this step entirely once subscription-service consumes Stripe webhooks.)
      } else {
        const paymentRequest: ProcessPaymentRequest = {
          userId: user?.id || 'onboarding-user',
          subscriptionId: subscription.id,
          amount: getTotalPrice(),
          currency,
          paymentGateway: planData.paymentGateway || 'MOCK',
          paymentMethod: paymentTab === 'digital-wallet' ? 'DIGITAL_WALLET' : 'BANK_TRANSFER',
          metadata: {
            billingEmail: form.email,
            billingName: `${form.firstName} ${form.lastName}`,
            couponCode: couponResult?.coupon?.code,
          },
        };
        await billingApi.processPayment(paymentRequest);
      }

      // Complete progress and show success
      completeCheckout();
    } catch (err) {
      // In local/non-prod: gracefully skip payment if backend is not configured
      // This allows onboarding to complete even without real payment gateways
      const errMsg = (err as Error)?.message || '';
      const isNonProdBypass =
        errMsg.includes('validation failed') ||
        errMsg.includes('User not found') ||
        errMsg.includes('not configured') ||
        errMsg.includes('NetworkError') ||
        errMsg.includes('Failed to fetch') ||
        errMsg.includes('already has an active subscription');

      if (isNonProdBypass) {
        console.warn(
          '[PaymentCheckout] Payment skipped (non-prod/stub mode):',
          errMsg
        );
        completeCheckout();
      } else {
        if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
        console.error('[PaymentCheckout] Payment failed:', err);
        setErrorMessage(
          errMsg || 'Payment processing failed. Please try again.'
        );
        setPageState('error');
      }
    }
  };

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    };
  }, []);

  // ---- Field error helper ----
  const fieldError = (field: keyof BillingFormData): string | undefined =>
    touched[field] ? errors[field] : undefined;

  // ========================================================================
  // RENDER: Loading
  // ========================================================================
  if (pageState === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted/30">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="flex flex-col items-center gap-4"
        >
          <BrandedLoading message="Loading checkout…" iconSize="md" />
        </motion.div>
      </div>
    );
  }

  // ========================================================================
  // RENDER: Processing
  // ========================================================================
  if (pageState === 'processing') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted/30">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col items-center gap-6 max-w-sm mx-auto px-6 text-center"
        >
          <div className="relative">
            <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
              <BrandedSpinner sizeClassName="h-8 w-8" className="shrink-0" />
            </div>
            <motion.div
              className="absolute -inset-2 rounded-full border-2 border-primary/20"
              animate={{ scale: [1, 1.15, 1], opacity: [0.5, 0, 0.5] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
          </div>
          <div>
            <h2 className="text-xl font-semibold mb-1">Processing your payment</h2>
            <p className="text-muted-foreground text-sm">
              Please don&apos;t close this page...
            </p>
          </div>
          <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-primary to-primary/80 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${processingProgress}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </motion.div>
      </div>
    );
  }

  // ========================================================================
  // RENDER: Success
  // ========================================================================
  if (pageState === 'success') {
    const sparklePositions = [
      { x: -60, y: -40 },
      { x: 50, y: -50 },
      { x: -45, y: 35 },
      { x: 55, y: 30 },
      { x: -20, y: -60 },
      { x: 30, y: 55 },
    ];

    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted/30">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex flex-col items-center gap-6 max-w-sm mx-auto px-6 text-center"
        >
          <div className="relative">
            {/* Sparkle particles */}
            {sparklePositions.map((pos, i) => (
              <motion.div
                key={i}
                custom={i}
                variants={sparkleVariants}
                initial="hidden"
                animate="visible"
                className="absolute left-1/2 top-1/2"
                style={{ x: pos.x, y: pos.y }}
              >
                <Sparkles className="h-4 w-4 text-yellow-500" />
              </motion.div>
            ))}

            {/* Ring */}
            <motion.div
              variants={successRingVariants}
              initial="hidden"
              animate="visible"
              className="h-20 w-20 rounded-full bg-emerald-500/10 border-2 border-emerald-500/30 flex items-center justify-center"
            >
              {/* Checkmark */}
              <motion.div
                variants={successCheckVariants}
                initial="hidden"
                animate="visible"
                className="h-12 w-12 rounded-full bg-emerald-500 flex items-center justify-center"
              >
                <Check className="h-7 w-7 text-white" strokeWidth={3} />
              </motion.div>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <h2 className="text-2xl font-bold mb-1">
              {isTrialPlan ? 'Trial Started!' : 'Payment Successful!'}
            </h2>
            <p className="text-muted-foreground text-sm">
              {isTrialPlan
                ? `Your ${planData?.trialDurationDays}-day free trial is now active.`
                : 'Your subscription is now active.'}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
            className="text-xs text-muted-foreground"
          >
            Redirecting you automatically...
          </motion.div>
        </motion.div>
      </div>
    );
  }

  // ========================================================================
  // RENDER: Error (standalone)
  // ========================================================================
  if (pageState === 'error' && !planData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted/30 px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col items-center gap-5 max-w-md mx-auto text-center"
        >
          <div className="h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center">
            <AlertCircle className="h-8 w-8 text-destructive" />
          </div>
          <div>
            <h2 className="text-xl font-semibold mb-1">Something went wrong</h2>
            <p className="text-muted-foreground text-sm">{errorMessage}</p>
          </div>
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Plan Selection
          </Button>
        </motion.div>
      </div>
    );
  }

  // ========================================================================
  // RENDER: Main Form
  // ========================================================================
  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
      {/* Top bar */}
      <div className="border-b border-border/50 bg-background/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center">
          <button
            onClick={onBack}
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
          <div className="ml-auto flex items-center gap-2 text-xs text-muted-foreground">
            <Lock className="h-3.5 w-3.5" />
            Secure Checkout
          </div>
        </div>
      </div>

      <motion.div
        variants={pageVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12"
      >
        {/* Page title */}
        <motion.div variants={staggerItem} className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
            Complete Your Order
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            {isTrialPlan
              ? 'Start your free trial — no charge today.'
              : 'Review your order and complete payment.'}
          </p>
        </motion.div>

        {/* Error banner (for retry after failed payment) */}
        <AnimatePresence>
          {pageState === 'error' && errorMessage && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mb-6"
            >
              <div className="bg-destructive/5 border border-destructive/20 rounded-xl p-4 flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-destructive">
                    Payment failed
                  </p>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    {errorMessage}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setPageState('form');
                    setErrorMessage(null);
                  }}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Two-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12">
          {/* ============================================================ */}
          {/* LEFT COLUMN: Payment Form (60%) */}
          {/* ============================================================ */}
          <motion.div
            variants={formShake ? shakeVariants : {}}
            animate={formShake ? 'shake' : undefined}
            className="lg:col-span-3 space-y-6"
          >
            <motion.div variants={staggerContainer} initial="hidden" animate="visible">
              {/* ---- Billing Information ---- */}
              <motion.div variants={staggerItem}>
                <Card className="border-border/60">
                  <CardHeader className="pb-4">
                    <CardTitle className="text-base font-semibold flex items-center gap-2">
                      <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">
                        1
                      </div>
                      Billing Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="checkout-email">Email address</Label>
                      <Input
                        id="checkout-email"
                        type="email"
                        placeholder="you@example.com"
                        value={form.email}
                        onChange={(e) => handleFieldChange('email', e.target.value)}
                        onBlur={() => handleFieldBlur('email')}
                        className={fieldError('email') ? 'border-destructive focus-visible:ring-destructive' : ''}
                      />
                      {fieldError('email') && (
                        <p className="text-xs text-destructive mt-1">{fieldError('email')}</p>
                      )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="checkout-first-name">First name</Label>
                        <Input
                          id="checkout-first-name"
                          placeholder="John"
                          value={form.firstName}
                          onChange={(e) => handleFieldChange('firstName', e.target.value)}
                          onBlur={() => handleFieldBlur('firstName')}
                          className={fieldError('firstName') ? 'border-destructive focus-visible:ring-destructive' : ''}
                        />
                        {fieldError('firstName') && (
                          <p className="text-xs text-destructive mt-1">{fieldError('firstName')}</p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="checkout-last-name">Last name</Label>
                        <Input
                          id="checkout-last-name"
                          placeholder="Doe"
                          value={form.lastName}
                          onChange={(e) => handleFieldChange('lastName', e.target.value)}
                          onBlur={() => handleFieldBlur('lastName')}
                          className={fieldError('lastName') ? 'border-destructive focus-visible:ring-destructive' : ''}
                        />
                        {fieldError('lastName') && (
                          <p className="text-xs text-destructive mt-1">{fieldError('lastName')}</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              {/* ---- Payment Method ---- */}
              <motion.div variants={staggerItem} className="mt-6">
                <Card className="border-border/60">
                  <CardHeader className="pb-4">
                    <CardTitle className="text-base font-semibold flex items-center gap-2">
                      <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">
                        2
                      </div>
                      Payment Method
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Tabs
                      value={paymentTab}
                      onValueChange={(v) => setPaymentTab(v as PaymentMethodTab)}
                    >
                      <TabsList className="w-full grid grid-cols-3 mb-4">
                        <TabsTrigger value="card" className="gap-1.5 text-xs sm:text-sm">
                          <CreditCard className="h-3.5 w-3.5" />
                          Card
                        </TabsTrigger>
                        <TabsTrigger value="digital-wallet" className="gap-1.5 text-xs sm:text-sm">
                          <Wallet className="h-3.5 w-3.5" />
                          Wallet
                        </TabsTrigger>
                        <TabsTrigger value="bank-transfer" className="gap-1.5 text-xs sm:text-sm">
                          <Building2 className="h-3.5 w-3.5" />
                          Bank
                        </TabsTrigger>
                      </TabsList>

                      {/* Card Tab — Stripe Elements (PCI-compliant, supports 3DS automatically) */}
                      <TabsContent value="card">
                        <motion.div
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.25 }}
                        >
                          {stripeReady ? (
                            <StripeCardForm
                              ref={stripeCardRef}
                              amount={getTotalPrice()}
                              currency={currency}
                              customerEmail={form.email}
                              customerId={user?.id}
                              subscriptionId={undefined}
                              planId={planData?.planCode}
                              description={`${planData?.displayName ?? 'Subscription'} (${billingCycle.toLowerCase()})`}
                              billingName={`${form.firstName} ${form.lastName}`.trim() || form.cardholderName}
                              onCardStateChange={({ complete, error }) => {
                                setStripeCardComplete(complete);
                                setStripeCardError(error);
                              }}
                            />
                          ) : (
                            <div className="rounded-xl border border-dashed border-border/60 px-4 py-6 text-center">
                              <p className="text-sm font-medium">Stripe is not configured</p>
                              <p className="mt-1 text-xs text-muted-foreground">
                                Set <code>VITE_STRIPE_PUBLIC_KEY</code> in the build environment to enable card payments.
                              </p>
                            </div>
                          )}
                        </motion.div>
                      </TabsContent>

                      {/* Digital Wallet Tab — single Stripe Payment Request button covering Apple Pay + Google Pay */}
                      <TabsContent value="digital-wallet">
                        {stripeReady ? (
                          <StripeWalletButton
                            amount={getTotalPrice()}
                            currency={currency}
                            label={`${planData?.displayName ?? 'Five AI'} — ${billingCycle.toLowerCase()}`}
                            country={planData?.countryCode ?? 'US'}
                            customerEmail={form.email}
                            customerId={user?.id}
                            subscriptionId={undefined}
                            planId={planData?.planCode}
                            description={`${planData?.displayName ?? 'Subscription'} (${billingCycle.toLowerCase()})`}
                            onPaymentSucceeded={() => completeCheckout()}
                            onPaymentFailed={(message) => {
                              setErrorMessage(message);
                              setPageState('error');
                            }}
                          />
                        ) : (
                          <div className="rounded-xl border border-dashed border-border/60 px-4 py-6 text-center">
                            <p className="text-sm text-muted-foreground">
                              Digital wallets unavailable — Stripe is not configured.
                            </p>
                          </div>
                        )}
                      </TabsContent>

                      {/* Bank Transfer Tab */}
                      <TabsContent value="bank-transfer">
                        <motion.div
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.25 }}
                          className="bg-muted/40 rounded-xl p-5 border border-border/60"
                        >
                          <div className="flex items-start gap-3">
                            <Info className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                            <div className="space-y-2">
                              <p className="text-sm font-medium">Bank Transfer Instructions</p>
                              <p className="text-sm text-muted-foreground leading-relaxed">
                                After submitting your order, you will receive an email with
                                bank transfer details. Your subscription will be activated
                                once payment is confirmed, typically within 1-2 business days.
                              </p>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground pt-1">
                                <Shield className="h-3.5 w-3.5" />
                                All transfers are processed through our secure banking partner.
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      </TabsContent>
                    </Tabs>
                  </CardContent>
                </Card>
              </motion.div>

              {/* ---- Coupon / Promo Code ---- */}
              <motion.div variants={staggerItem} className="mt-6">
                <Card className="border-border/60">
                  <CardContent className="py-4">
                    <button
                      type="button"
                      onClick={() => setCouponExpanded(!couponExpanded)}
                      className="w-full flex items-center justify-between text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <span className="flex items-center gap-2">
                        <Tag className="h-4 w-4" />
                        Have a promo code?
                      </span>
                      {couponExpanded ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </button>

                    <AnimatePresence>
                      {couponExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <div className="pt-4 space-y-3">
                            {couponResult?.valid ? (
                              <div className="flex items-center justify-between bg-emerald-500/5 border border-emerald-500/20 rounded-lg px-4 py-3">
                                <div className="flex items-center gap-2">
                                  <Check className="h-4 w-4 text-emerald-500" />
                                  <span className="text-sm font-medium text-emerald-700 dark:text-emerald-400">
                                    {couponResult.coupon?.code}
                                  </span>
                                  <Badge
                                    variant="secondary"
                                    className="text-xs bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                                  >
                                    {couponResult.coupon?.discountType === 'PERCENTAGE'
                                      ? `${couponResult.coupon.discountValue}% off`
                                      : `${formatCurrency(couponResult.coupon?.discountValue ?? 0, currency)} off`}
                                  </Badge>
                                </div>
                                <button
                                  onClick={handleRemoveCoupon}
                                  className="text-muted-foreground hover:text-foreground"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            ) : (
                              <>
                                <div className="flex gap-2">
                                  <Input
                                    placeholder="Enter promo code"
                                    value={couponCode}
                                    onChange={(e) => {
                                      setCouponCode(e.target.value.toUpperCase());
                                      setCouponError(null);
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') handleApplyCoupon();
                                    }}
                                    className="flex-1"
                                  />
                                  <Button
                                    variant="outline"
                                    onClick={handleApplyCoupon}
                                    disabled={!couponCode.trim() || couponLoading}
                                    isLoading={couponLoading}
                                  >
                                    Apply
                                  </Button>
                                </div>
                                {couponError && (
                                  <p className="text-xs text-destructive">{couponError}</p>
                                )}
                              </>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </CardContent>
                </Card>
              </motion.div>

              {/* ---- Security Badges ---- */}
              <motion.div variants={staggerItem} className="mt-6">
                <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 py-4">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Shield className="h-4 w-4 text-emerald-500" />
                    <span>256-bit SSL Encryption</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Lock className="h-4 w-4 text-emerald-500" />
                    <span>PCI DSS Compliant</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <RefreshCw className="h-4 w-4 text-emerald-500" />
                    <span>Money-back Guarantee</span>
                  </div>
                </div>
              </motion.div>

              {/* ---- Submit Button (mobile) ---- */}
              <motion.div variants={staggerItem} className="mt-6 lg:hidden">
                <Button
                  size="lg"
                  className={`w-full h-13 text-base font-semibold rounded-xl shadow-lg transition-all ${
                    isTrialPlan
                      ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white'
                      : 'bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-primary-foreground'
                  }`}
                  disabled={!isFormValid}
                  onClick={handleSubmit}
                >
                  {isTrialPlan
                    ? 'Start Free Trial'
                    : `Pay ${formatCurrency(getTotalPrice(), currency)}`}
                </Button>
              </motion.div>
            </motion.div>
          </motion.div>

          {/* ============================================================ */}
          {/* RIGHT COLUMN: Order Summary (40%) */}
          {/* ============================================================ */}
          <motion.div
            variants={staggerItem}
            initial="hidden"
            animate="visible"
            className="lg:col-span-2"
          >
            <div className="lg:sticky lg:top-20">
              <Card className="border-border/60 overflow-hidden">
                {/* Gradient accent bar */}
                <div className="h-1 bg-gradient-to-r from-primary via-primary/80 to-primary/60" />

                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base font-semibold">
                      Order Summary
                    </CardTitle>
                    {isTrialPlan && (
                      <Badge className="bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20 text-xs">
                        Free Trial
                      </Badge>
                    )}
                  </div>
                </CardHeader>

                <CardContent className="space-y-5">
                  {/* Plan info */}
                  <div className="bg-muted/30 rounded-xl p-4 border border-border/40">
                    <div className="flex items-start justify-between mb-1">
                      <div>
                        <p className="font-semibold text-sm">
                          {planData?.displayName || planData?.planCode || 'Selected Plan'}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {billingCycle === 'YEARLY' ? 'Annual' : 'Monthly'} subscription
                        </p>
                      </div>
                      <Sparkles className="h-5 w-5 text-primary/50" />
                    </div>
                  </div>

                  {/* Billing Cycle Toggle */}
                  {planData?.monthlyPrice != null && planData?.yearlyPrice != null && (
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Billing Cycle
                      </p>
                      <div className="flex rounded-lg bg-muted/50 p-1">
                        <button
                          type="button"
                          onClick={() => handleBillingCycleToggle('MONTHLY')}
                          className={`flex-1 text-xs font-medium py-2 px-3 rounded-md transition-all ${
                            billingCycle === 'MONTHLY'
                              ? 'bg-background text-foreground shadow-sm'
                              : 'text-muted-foreground hover:text-foreground'
                          }`}
                        >
                          Monthly
                        </button>
                        <button
                          type="button"
                          onClick={() => handleBillingCycleToggle('YEARLY')}
                          className={`flex-1 text-xs font-medium py-2 px-3 rounded-md transition-all relative ${
                            billingCycle === 'YEARLY'
                              ? 'bg-background text-foreground shadow-sm'
                              : 'text-muted-foreground hover:text-foreground'
                          }`}
                        >
                          Yearly
                          {yearlySavings() !== null && (
                            <span className="ml-1 text-[10px] font-semibold text-emerald-600 dark:text-emerald-400">
                              Save {formatCurrency(yearlySavings()!, currency)}
                            </span>
                          )}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Price Breakdown */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">
                        {planData?.displayName || 'Plan'}{' '}
                        <span className="text-xs">
                          ({billingCycle === 'YEARLY' ? '/year' : '/month'})
                        </span>
                      </span>
                      <span className="font-medium">
                        {formatCurrency(getBasePrice(), currency)}
                      </span>
                    </div>

                    {getDiscountAmount() > 0 && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className="flex items-center justify-between text-sm"
                      >
                        <span className="text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                          <Tag className="h-3.5 w-3.5" />
                          Discount
                        </span>
                        <span className="text-emerald-600 dark:text-emerald-400 font-medium">
                          -{formatCurrency(getDiscountAmount(), currency)}
                        </span>
                      </motion.div>
                    )}

                    {/* Divider */}
                    <div className="border-t border-border/60" />

                    {/* Total */}
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold">
                        {isTrialPlan ? 'Due today' : 'Total'}
                      </span>
                      <div className="text-right">
                        <span className="text-lg font-bold">
                          {isTrialPlan
                            ? formatCurrency(0, currency)
                            : formatCurrency(getTotalPrice(), currency)}
                        </span>
                        {isTrialPlan && getTotalPrice() > 0 && (
                          <p className="text-xs text-muted-foreground">
                            Then {formatCurrency(getTotalPrice(), currency)}
                            {billingCycle === 'YEARLY' ? '/year' : '/month'}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Trial Badge */}
                  {isTrialPlan && planData?.trialDurationDays && (
                    <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-3 flex items-start gap-2.5">
                      <Info className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />
                      <p className="text-xs text-emerald-700 dark:text-emerald-400 leading-relaxed">
                        <span className="font-semibold">
                          {planData.trialDurationDays}-day free trial
                        </span>{' '}
                        included. You won&apos;t be charged until the trial ends. Cancel
                        anytime.
                      </p>
                    </div>
                  )}

                  {/* Features List */}
                  {planData?.features && planData.features.length > 0 && (
                    <div className="space-y-2.5 pt-1">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        What&apos;s Included
                      </p>
                      <ul className="space-y-2">
                        {planData.features.map((feature, idx) => (
                          <motion.li
                            key={idx}
                            initial={{ opacity: 0, x: -8 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.3 + idx * 0.05 }}
                            className="flex items-start gap-2.5 text-sm"
                          >
                            <div className="h-4.5 w-4.5 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                              <Check className="h-3 w-3 text-primary" />
                            </div>
                            <span className="text-muted-foreground leading-tight">
                              {feature}
                            </span>
                          </motion.li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Submit Button (desktop) */}
                  <div className="hidden lg:block pt-2">
                    <Button
                      size="lg"
                      className={`w-full h-12 text-sm font-semibold rounded-xl shadow-lg transition-all ${
                        isTrialPlan
                          ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white'
                          : 'bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-primary-foreground'
                      }`}
                      disabled={!isFormValid}
                      onClick={handleSubmit}
                    >
                      {isTrialPlan ? (
                        <>
                          <Sparkles className="h-4 w-4 mr-1.5" />
                          Start Free Trial
                        </>
                      ) : (
                        `Pay ${formatCurrency(getTotalPrice(), currency)}`
                      )}
                    </Button>
                    <p className="text-[11px] text-muted-foreground text-center mt-3 leading-relaxed">
                      By continuing, you agree to our{' '}
                      <a href="/terms" className="underline underline-offset-2 hover:text-foreground">
                        Terms of Service
                      </a>{' '}
                      and{' '}
                      <a href="/privacy" className="underline underline-offset-2 hover:text-foreground">
                        Privacy Policy
                      </a>
                      .
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default PaymentCheckoutPage;
